import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import React, { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { API_CUSTOM_SEGMENTS_ASSOCIATED_ADGROUPS_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { formatDate } from '../../util/helpers';
import { getRequestJSON } from '../../util/services';

interface AssociatedAdGroup {
  id: number;
  name: string;
  insertion_order_id: number;
  status: string;
  start_date: string;
  end_date: string;
}

interface AssociatedAdGroupsResponse {
  collection: AssociatedAdGroup[];
  pagination: {
    total_items: number;
    per_page: number;
    current_page: number;
  };
}

interface AssociatedAdGroupsProps {
  segmentId: number;
}

function AssociatedAdGroups({ segmentId }: AssociatedAdGroupsProps) {
  const { apiHost } = useConfig();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10000);

  const { data, isLoading, isError, error } = useQuery<AssociatedAdGroupsResponse>({
    queryKey: ['associatedAdGroups', segmentId, page + 1, perPage],
    queryFn: () => {
      const params: any = {
        page: (page + 1).toString(),
        per_page: perPage.toString(),
      };

      return getRequestJSON(
        apiHost,
        `${generatePath(API_CUSTOM_SEGMENTS_ASSOCIATED_ADGROUPS_URL, {
          segmentId: segmentId.toString(),
        })}?${queryString.stringify(params)}`
      );
    },
    enabled: !!segmentId,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const totalItems = data?.pagination?.total_items || 0;
  const adGroups = data?.collection || [];

  return (
    <Box mt={2} p={2} sx={{ background: 'white', borderRadius: '5px' }}>
      <Typography variant="h6">Associated Ad Groups</Typography>

      <Stack spacing={2} mt={2}>
        {/* eslint-disable no-nested-ternary */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box py={2}>
            <Typography color="error">
              Error loading associated ad groups: {error?.toString()}
            </Typography>
          </Box>
        ) : adGroups.length === 0 ? (
          <Box py={2}>
            <Typography color="text.secondary">
              No associated ad groups found
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign ID</TableCell>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Insertion Order ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adGroups.map((adGroup) => (
                    <TableRow key={adGroup.id} hover>
                      <TableCell>{adGroup.id}</TableCell>
                      <TableCell>{adGroup.name}</TableCell>
                      <TableCell>{adGroup.insertion_order_id}</TableCell>
                      <TableCell>
                        <span
                          style={{
                            color:
                              // eslint-disable-next-line no-nested-ternary
                              adGroup.status === 'active'
                                ? '#4caf50'
                                : adGroup.status === 'paused'
                                  ? '#ff9800'
                                  : '#757575',
                            fontWeight: 500,
                          }}
                        >
                          {adGroup.status}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(adGroup.start_date) || '-'}</TableCell>
                      <TableCell>{formatDate(adGroup.end_date) || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={perPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
        {/* eslint-enable no-nested-ternary */}
      </Stack>
    </Box>
  );
}

export default AssociatedAdGroups;
